import { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import type { UserOut } from "@/types/api";
import { logoutApi } from "@/api/auth";
import { queryClient } from "@/api/queryClient";
import { getMe } from "@/api/users";
import {
  cacheLastKnownUser,
  clearLastKnownUser,
  getCurrentUserId,
  getLastKnownUser,
  setCurrentUserId,
} from "@/offline/currentUser";
import { clearOfflineDataForUser } from "@/offline/db";
import { isNetworkError, onNetworkChange } from "@/offline/network";
import { refreshPendingCounts } from "@/offline/syncEngine";
import { isNativeApp } from "./nativeSession";
import { clearPendingLogout, finishPendingLogout, hasPendingLogout, markLogoutPending } from "./pendingLogout";

interface AuthContextValue {
  user: UserOut | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserOut | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    // A web sign-out made offline hasn't cleared the session cookies yet, so
    // getMe() would still succeed. Stay signed out and finish it if online.
    if (hasPendingLogout()) {
      await finishPendingLogout();
      setUser(null);
      setCurrentUserId(null);
      clearLastKnownUser();
      return;
    }
    try {
      const u = await getMe();
      setUser(u);
      setCurrentUserId(u.id);
      cacheLastKnownUser(u);
    } catch (err) {
      // A cold start with no connection must not look like a logout — that's
      // exactly the crag/gym scenario offline mode exists for. Only fall back
      // to the last-known user on a genuine network failure; a real auth
      // rejection (expired session) still logs out as before (and the axios
      // interceptor already redirects to /login on a failed refresh).
      const cached = isNetworkError(err) ? getLastKnownUser() : null;
      setUser(cached);
      setCurrentUserId(cached?.id ?? null);
      if (!cached) clearLastKnownUser();
    }
  }, []);

  const login = useCallback(async () => {
    // A fresh sign-in replaced the old cookies, so nothing is left to finish.
    clearPendingLogout();
    await refreshUser();
  }, [refreshUser]);

  const logout = useCallback(() => {
    const userId = getCurrentUserId();
    setUser(null);
    setCurrentUserId(null);
    clearLastKnownUser();
    // Drop cached server data and this user's offline queue, so the next
    // account on this device never sees them. Only an explicit logout does
    // this: an expired session keeps the queue for the next sign-in.
    queryClient.clear();
    if (userId != null) {
      clearOfflineDataForUser(userId)
        .then(refreshPendingCounts)
        .catch((err) => Sentry.captureException(err));
    }
    // A 401 means the session was already dead, which is what signing out
    // wanted anyway; logoutApi() clears the local session regardless. On the
    // web, an offline sign-out can't clear the cookies, so it's finished later.
    logoutApi().catch((err) => {
      if (isAxiosError(err) && err.response?.status === 401) return;
      if (isNetworkError(err)) {
        if (!isNativeApp()) markLogoutPending();
        return;
      }
      Sentry.captureException(err);
    });
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  useEffect(
    () =>
      onNetworkChange((online) => {
        if (online) void finishPendingLogout();
      }),
    []
  );

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
